export interface AudioCallTelemetryEntry {
  order_id: number | null;
  session_id: string;
  seq: number;
  timestamp: number;
  elapsed_ms: number;
  role: 'customer' | 'executor' | null;
  source: string;
  stage: string;
  tone: string;
  metrics: Record<string, number | string | boolean>;
}

export interface AudioCallTelemetryBatch {
  version: 1;
  batch_id: string;
  entries: AudioCallTelemetryEntry[];
}

export interface AudioCallTelemetryAck {
  accepted: boolean;
  batch_id?: string;
}

// Keep in sync with pyjam_chat/src/services/audio-call-diagnostics.ts.
export const AUDIO_CALL_NUMERIC_METRICS: readonly string[] = [
  'attempt', 'maxAttempts', 'reconnectAttempts', 'maxReconnectAttempts', 'timeoutMs',
  'rtt_ms', 'inbound_packets', 'outbound_packets', 'inbound_bytes', 'outbound_bytes',
  'packets_lost', 'jitter_ms', 'audio_level', 'total_audio_energy', 'concealed_samples',
  'ice_error_code', 'ice_server_index', 'dropped_events', 'snapshot_age_ms',
];

export const AUDIO_CALL_ENUM_METRICS: Readonly<Record<string, readonly string[]>> = {
  connectionState: ['new', 'connecting', 'connected', 'disconnected', 'failed', 'closed'],
  iceState: ['new', 'checking', 'connected', 'completed', 'disconnected', 'failed', 'closed'],
  local_candidate_type: ['host', 'srflx', 'prflx', 'relay'],
  remote_candidate_type: ['host', 'srflx', 'prflx', 'relay'],
  protocol: ['udp', 'tcp'],
  relay_protocol: ['udp', 'tcp', 'tls'],
  turn_alias: ['timeweb', 'app_geo', 'dev'],
  client_platform: ['ios_app', 'android_app', 'ios_web', 'android_web', 'web'],
  stopReason: ['manual', 'system_end_action', 'route_guard_stop', 'call_init_failure', 'invalid_call_state', 'response_declined', 'reconnect_failed', 'backend_exit', 'partner_exit'],
  error_name: ['Error', 'NotAllowedError', 'NotFoundError', 'NotReadableError', 'AbortError', 'InvalidStateError', 'OperationError', 'TimeoutError'],
};

export function audioCallSafeMetrics(detail: unknown): Record<string, number | string | boolean> {
  const result: Record<string, number | string | boolean> = {};
  if (!detail || typeof detail !== 'object') return result;
  for (const key of AUDIO_CALL_NUMERIC_METRICS) {
    const value: unknown = Reflect.get(detail, key);
    if (typeof value === 'number' && Number.isFinite(value)) result[key] = value;
  }
  for (const [key, values] of Object.entries(AUDIO_CALL_ENUM_METRICS)) {
    const value: unknown = detail instanceof Error && key === 'error_name' ? detail.name : Reflect.get(detail, key);
    if (typeof value === 'string' && values.includes(value)) result[key] = value;
  }
  const version: unknown = Reflect.get(detail, 'app_version');
  if (typeof version === 'string' && /^\d{1,4}(?:\.\d{1,4}){1,3}$/.test(version)) result['app_version'] = version;
  return result;
}
